import { apiError } from "../../../../../../utils/formErrors";
import TableSkeleton from "../../../../../../component/TableSkeleton";
import React, { useState, useEffect } from "react";
import { Box, Stack, Table, TableHead, TableBody, TableFooter, TableRow, TableCell, CircularProgress, Dialog, Select, MenuItem, IconButton } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useParams, useNavigate } from "react-router";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import TopBar from "../../../../../../component/TopBar";
import MainBox from "../../../../container/MainBox";
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import FormField from "../../../../../../component/Formfield";
import TypeBtn from "../../../../../../component/AddRemoveButton";
import EmptyState from "../../../../../../component/EmptyState";
import { StatCard, StatusChip, selectSx, headCellSx, bodyCellSx } from "../../../../../../component/listing";
import { getPayrollRunById, reviewPayrollRun, updatePayrollRunItems, markPayrollRunPaid, resyncRunExpenses, downloadRunBankFile } from "../../../../../../services/payroll";
import RunAuditPanel from "./RunAuditPanel";
import TablePager from "../../../../../../component/TablePager";
import { pageSlice, clampPage } from "../../../../../../component/pageRange";

import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../../theme";
import { money, periodLabel, RUN_STATUS_TONE, RUN_STATUS_LABEL, lineStatus } from "../payrollUtils";
import { moneyExact, runTotals, runWarnings, columnTotals } from "../payrollFacts";

import { pressable } from "../../../../../../component/pressableProps";
import { counterRowSx } from "../../../../container/frameLayout";
import { toneColors } from "../../../../../../component/statusTone";
import InlineMessage from "../../../../../../component/InlineMessage";
const nameOf = (it) => it.employee ? `${it.employee.fname || ""} ${it.employee.lname || ""}`.trim() : (it.employeeName || "—");
const eid = (e) => String(e?._id || e);
const round = (n) => Math.round((Number(n) || 0) * 100) / 100;

// The totals row sits on the page blush inside the table's own border, so its
// cells carry no hairline of their own.
const FOOT_SX = {
  borderBottom: "none",
  fontSize: "0.875rem",
  fontWeight: 700,
  color: tokens.text,
  fontVariantNumeric: "tabular-nums",
  py: "0.875rem",
};

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "");

// clickable chip for an outstanding claim/encashment that deep-links to that request
const pillSx = (status) => {
  // The book's badge: a picked tint behind its text variant, at 5px/12px on a
  // pill. Derived alpha over the blush page goes muddy.
  const { fg, bg } = toneColors(
    status === "rejected" ? "danger" : status === "approved" ? "success" : "muted"
  );
  return {
    display: "inline-flex", alignItems: "center", gap: "0.25rem", cursor: "pointer",
    fontSize: "0.8125rem", fontWeight: 600, color: fg,
    background: bg, borderRadius: "999px", padding: "5px 12px",
  };
};

// Full earnings/deductions breakdown for one run line, so a reviewer can see
// exactly how net was reached before approving — with reimbursements shown as
// reviewable claims (provenance + link) and outstanding claims surfaced.
const BreakdownPanel = ({ it }) => {
  const navigate = useNavigate();
  // plain salary earnings + earning adjustments (reimbursements & encashments rendered separately, richer)
  const earnings = [...(it.earnings || [])];
  (it.adjustments || []).filter((a) => a.type === "EARNING").forEach((a) => earnings.push({ name: `${a.name} (adj)`, amount: a.amount }));
  const reimbursements = it.reimbursements || [];
  const encashments = it.encashments || [];
  const deductions = [...(it.deductions || [])];
  (it.adjustments || []).filter((a) => a.type === "DEDUCTION").forEach((a) => deductions.push({ name: `${a.name} (adj)`, amount: a.amount }));
  if (it.lopAmount > 0) {
    const s = it.lopSource;
    const detail = s ? [s.unpaidLeaveDays ? `${s.unpaidLeaveDays}d unpaid leave` : "", s.absenceDays ? `${s.absenceDays}d absence` : "", s.leaveShortfallDays ? `${s.leaveShortfallDays}d leave shortfall` : ""].filter(Boolean).join(" + ") : `${it.lopDays}d`;
    deductions.push({ name: `Loss of Pay (${detail})`, amount: it.lopAmount });
  }
  if (it.prorationAmount > 0) {
    deductions.push({ name: `Proration (${it.prorationDays}d not employed)`, amount: it.prorationAmount });
  }

  const PlainLine = ({ name, amount }) => (
    <Stack direction="row" justifyContent="space-between" sx={{ py: "0.25rem", fontSize: "0.8125rem" }}>
      <Box sx={{ color: tokens.textMuted }}>{name}</Box><Box>{money(amount)}</Box>
    </Stack>
  );
  const totalEarn = round([...earnings, ...reimbursements, ...encashments].reduce((s, e) => s + (Number(e.amount) || 0), 0));
  const totalDed = round(deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0));
  const pc = it.pendingClaims;
  const pe = it.pendingEncashments;

  return (
    <Box sx={{ background: tokens.surfaceSubtle, p: "1.25rem 1.5rem" }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        {/* Earnings + reviewable reimbursements */}
        <Box sx={{ flex: 1.2, minWidth: "16rem" }}>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em", color: tokens.successText, mb: "0.5rem" }}>Earnings + Additions</Box>
          {earnings.map((l, i) => <PlainLine key={i} name={l.name} amount={l.amount} />)}
          {reimbursements.map((r, i) => (
            <Box key={`r${i}`} sx={{ py: "0.25rem", borderTop: i === 0 && earnings.length ? `1px dashed ${tokens.border}` : "none", mt: i === 0 && earnings.length ? "0.3rem" : 0 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ fontSize: "0.8125rem" }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: "0.4rem", height: "0.4rem", borderRadius: "50%", background: tokens.accent }} />
                  <Box sx={{ color: tokens.text, fontWeight: 500 }}>{r.name}</Box>
                </Stack>
                <Box>{money(r.amount)}</Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ pl: "1rem", mt: "0.25rem", fontSize: "0.8125rem", color: tokens.textMuted }}>
                <span>{r.category || "Expense"}{r.date ? ` · ${fmtDate(r.date)}` : ""}</span>
                {r.hasReceipt && <Stack direction="row" alignItems="center" spacing={0.25}><ReceiptLongOutlinedIcon sx={{ fontSize: "0.8rem" }} /><span>receipt</span></Stack>}
                {r.expenseId && (
                  <Box component="span" {...pressable(() => navigate(`/app/expense-requests-details/${r.expenseId}`))}
                    sx={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", color: tokens.accent, cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
                    view claim <OpenInNewIcon sx={{ fontSize: "0.7rem" }} />
                  </Box>
                )}
              </Stack>
            </Box>
          ))}
          {encashments.map((e, i) => (
            <Box key={`e${i}`} sx={{ py: "0.25rem", borderTop: i === 0 && (earnings.length || reimbursements.length) ? `1px dashed ${tokens.border}` : "none", mt: i === 0 && (earnings.length || reimbursements.length) ? "0.3rem" : 0 }}>
              <Stack direction="row" justifyContent="space-between" sx={{ fontSize: "0.8125rem" }}>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Box sx={{ width: "0.4rem", height: "0.4rem", borderRadius: "50%", background: tokens.success }} />
                  <Box sx={{ color: tokens.text, fontWeight: 500 }}>{e.name}</Box>
                </Stack>
                <Box>{money(e.amount)}</Box>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ pl: "1rem", mt: "0.25rem", fontSize: "0.8125rem", color: tokens.textMuted }}>
                <span>{e.days} day(s){e.amount && e.days ? ` × ${money(round(e.amount / e.days))}/day` : ""}</span>
                <Box component="span" {...pressable(() => navigate(e.encashmentId ? `/app/leave-encashment?id=${e.encashmentId}` : "/app/leave-encashment"))}
                  sx={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", color: tokens.accent, cursor: "pointer", fontWeight: 600, "&:hover": { textDecoration: "underline" } }}>
                  view <OpenInNewIcon sx={{ fontSize: "0.7rem" }} />
                </Box>
              </Stack>
            </Box>
          ))}
          <Stack direction="row" justifyContent="space-between" sx={{ pt: "0.5rem", mt: "0.25rem", borderTop: `1px solid ${tokens.border}`, fontSize: "0.8125rem", fontWeight: 600 }}>
            <Box>Total</Box><Box>{money(totalEarn)}</Box>
          </Stack>
        </Box>

        {/* Deductions */}
        <Box sx={{ flex: 1, minWidth: "14rem" }}>
          <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em", color: tokens.danger, mb: "0.5rem" }}>Deductions</Box>
          {deductions.length === 0 ? <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>None</Box> : deductions.map((l, i) => <PlainLine key={i} name={l.name} amount={l.amount} />)}
          <Stack direction="row" justifyContent="space-between" sx={{ pt: "0.5rem", mt: "0.25rem", borderTop: `1px solid ${tokens.border}`, fontSize: "0.8125rem", fontWeight: 600 }}>
            <Box>Total</Box><Box>{money(totalDed)}</Box>
          </Stack>
        </Box>

        {/* Employer */}
        {(it.employerContributions || []).length > 0 && (
          <Box sx={{ flex: 1, minWidth: "14rem" }}>
            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.03em", color: tokens.accent, mb: "0.5rem" }}>Employer (CTC)</Box>
            {it.employerContributions.map((l, i) => <PlainLine key={i} name={l.name} amount={l.amount} />)}
            <Stack direction="row" justifyContent="space-between" sx={{ pt: "0.5rem", mt: "0.25rem", borderTop: `1px solid ${tokens.border}`, fontSize: "0.8125rem", fontWeight: 600 }}>
              <Box>Total</Box><Box>{money(it.employerTotal)}</Box>
            </Stack>
          </Box>
        )}
      </Stack>

      {/* outstanding claims not in this run — each links to that specific claim */}
      {pc && (pc.pending?.count > 0 || pc.rejected?.count > 0) && (
        <InlineMessage tone="warning" sx={{ mt: "1rem" }}>
          <Box sx={{ mb: "0.5rem" }}>
            {pc.pending?.count > 0 && <><b>{pc.pending.count}</b> pending claim(s) ({money(pc.pending.total)}) not included{pc.rejected?.count > 0 ? " · " : ""}</>}
            {pc.rejected?.count > 0 && <Box component="span" sx={{ color: tokens.textMuted }}>{pc.rejected.count} rejected this period</Box>}
          </Box>
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            {(pc.items || []).map((c) => (
              <Box key={c.id} component="span" {...pressable(() => navigate(`/app/expense-requests-details/${c.id}`))} sx={pillSx(c.status)}>
                {c.category} · {money(c.amount)} <OpenInNewIcon sx={{ fontSize: "0.7rem" }} />
              </Box>
            ))}
          </Stack>
        </InlineMessage>
      )}

      {/* outstanding encashments not in this run — each links to that specific request */}
      {pe && (pe.pending?.count > 0 || pe.approvedNotIncluded?.count > 0) && (
        <InlineMessage tone="warning" sx={{ mt: "0.75rem" }}>
          <Box sx={{ mb: "0.5rem" }}>
            {pe.approvedNotIncluded?.count > 0 && <><b>{pe.approvedNotIncluded.count}</b> approved encashment(s) ({money(pe.approvedNotIncluded.total)}) not included — re-sync{pe.pending?.count > 0 ? " · " : ""}</>}
            {pe.pending?.count > 0 && <Box component="span" sx={{ color: tokens.textMuted }}>{pe.pending.count} pending HR approval ({money(pe.pending.total)})</Box>}
          </Box>
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            {(pe.items || []).map((e) => (
              <Box key={e.id} component="span" {...pressable(() => navigate(`/app/leave-encashment?id=${e.id}`))} sx={pillSx(e.status)}>
                {e.leaveType} · {e.days}d · {money(e.amount)} <OpenInNewIcon sx={{ fontSize: "0.7rem" }} />
              </Box>
            ))}
          </Stack>
        </InlineMessage>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: "0.75rem", fontSize: "0.875rem", fontWeight: 600 }}>
        <Box sx={{ color: tokens.textMuted, mr: 1 }}>Net pay:</Box><Box sx={{ color: it.negativeNet ? tokens.danger : tokens.successText }}>{moneyExact(it.netPay)}</Box>
      </Stack>
      {it.negativeNet && (
        <Box sx={{ mt: "0.5rem", fontSize: "0.8125rem", color: tokens.danger }}>⚠ Net is negative — deductions exceed pay. Reduce the recovery or carry it to a later cycle.</Box>
      )}
    </Box>
  );
};

// vs-last-cycle indicator: "New" joiner, or a +/- net delta (spike highlighted)

// vs-last-cycle indicator: "New" joiner, or a +/- net delta (spike highlighted)
const VarianceBadge = ({ v }) => {
  if (!v) return null;
  if (v.flag === "new") return <Box component="span" sx={{ ml: 1, fontSize: "0.8125rem", fontWeight: 600, color: tokens.accent, background: `${tokens.accentTint}`, borderRadius: "6px", px: 0.5, py: 0.5 }}>New</Box>;
  if (v.deltaPct == null || v.deltaPct === 0) return null;
  const up = v.deltaPct > 0;
  const spike = v.flag === "spike";
  const color = spike ? tokens.warning : tokens.textMuted;
  return (
    <Box component="span" title={`Last cycle: ${money(v.prevNet)}`}
      sx={{ ml: 1, fontSize: "0.8125rem", fontWeight: 600, color, background: spike ? tokens.warningTint : "transparent", borderRadius: "6px", px: spike ? 0.625 : 0, py: 0.5 }}>
      {up ? "▲" : "▼"} {Math.abs(v.deltaPct)}%
    </Box>
  );
};

const PayrollRunDetail = () => {
  const { id } = useParams();
  const notify = useNotify();
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editItem, setEditItem] = useState(null); // line being edited
  const [lopDays, setLopDays] = useState(0);
  const [adjustments, setAdjustments] = useState([]);
  const [editReason, setEditReason] = useState("");
  const [expanded, setExpanded] = useState(null); // row index whose breakdown is open
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchRun = async () => {
    setLoading(true);
    try {
      const res = await getPayrollRunById(id);
      if (res?.success === false) throw new Error(res?.message || "Failed to load run");
      setRun(res?.data || null);
    } catch (e) {
      notify.error(e?.message || "Failed to load run");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRun(); /* eslint-disable-next-line */ }, [id]);

  const review = async (status) => {
    setSubmitting(true);
    try {
      const res = await reviewPayrollRun(id, { status });
      if (res?.success === false) throw new Error(res?.message || "Could not update run");
      notify.success(status === "approved" ? "Payroll approved — payslips generated" : "Payroll rejected");
      fetchRun();
    } catch (e) { notify.error(e?.message || "Could not update run"); }
    finally { setSubmitting(false); }
  };

  const resync = async () => {
    setSubmitting(true);
    try {
      const res = await resyncRunExpenses(id);
      if (res?.success === false) throw apiError(res, "Could not re-sync");
      const bits = [];
      if (res.added > 0) bits.push(`${res.added} expense(s)`);
      if (res.encAdded > 0) bits.push(`${res.encAdded} encashment(s)`);
      notify.success(bits.length ? `Pulled in ${bits.join(" + ")}` : "Nothing new to add");
      if (res.orphanCount > 0) notify.error(`${res.orphanCount} approved item(s) belong to employees with no salary structure — not included.`);
      fetchRun();
    } catch (e) { notify.error(e?.message || "Could not re-sync"); }
    finally { setSubmitting(false); }
  };

  const markPaid = async () => {
    const paymentReference = window.prompt("Payment reference (UTR / batch no.) — optional:") || undefined;
    setSubmitting(true);
    try {
      const res = await markPayrollRunPaid(id, { paymentReference });
      if (res?.success === false) throw apiError(res, "Could not mark paid");
      notify.success("Payroll marked as paid");
      fetchRun();
    } catch (e) { notify.error(e?.message || "Could not mark paid"); }
    finally { setSubmitting(false); }
  };

  // Asked for, not assembled. Building this in the page meant every account
  // number in the organisation had to be fetched and cached first, whether or
  // not anyone ever pressed the button.
  const exportBankFile = async () => {
    try {
      const res = await downloadRunBankFile(id);
      if (!res?.success) throw new Error(res?.message || "Could not generate bank file");
      const url = URL.createObjectURL(res.blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.filename || `bank_advice_${periodLabel(run.month, run.year).replace(" ", "_")}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { notify.error(e?.message || "Could not generate bank file"); }
  };

  const openEdit = (it) => {
    setEditItem(it);
    setLopDays(it.lopDays || 0);
    setAdjustments((it.adjustments || []).map((a) => ({ ...a })));
    setEditReason("");
  };

  const saveEdit = async () => {
    setSubmitting(true);
    try {
      const payload = { reason: editReason, items: [{ employee: eid(editItem.employee), lopDays: Number(lopDays) || 0, adjustments: adjustments.filter((a) => a.name && a.amount) }] };
      const res = await updatePayrollRunItems(id, payload);
      if (res?.success === false) throw apiError(res, "Could not save changes");
      notify.success("Line updated");
      setEditItem(null);
      setRun(res.data);
    } catch (e) { notify.error(e?.message || "Could not save changes"); }
    finally { setSubmitting(false); }
  };

  const exportCsv = () => {
    // Status travels with the export: the reason to take this file away is
    // usually to work out who is not going to be paid, and that answer must not
    // live only on the screen.
    const header = ["Employee", "CTC", "Gross", "Deductions", "LOP Days", "Net Pay", "Status"];
    const lines = run.items.map((it) => [
      nameOf(it), it.ctc, it.grossPay, it.totalDeductions, it.lopDays || 0, it.netPay,
      lineStatus(it, run.status).label,
    ]);
    const csv = [header, ...lines].map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `payroll_${periodLabel(run.month, run.year).replace(" ", "_")}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    // The header and the column widths are known before the data is, so they
    // render immediately and nothing shifts when the rows land. The Edit column
    // is not among them: whether this run can still be edited depends on its
    // status, which is exactly what has not loaded yet, so the skeleton states
    // the seven columns every run has and lets the eighth appear with the data.
    return (
      <Box sx={{ width: "100%" }}>
        <TopBar MainHeading={"Payroll"} />
        <MainBox sx={{ padding: "1.25rem" }}>
          <Box
            sx={{
              background: tokens.card,
              border: `1px solid ${tokens.border}`,
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <Table sx={{ width: "100%", tableLayout: "fixed" }} aria-busy="true">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...headCellSx, width: "26%" }}>Employee</TableCell>
                  <TableCell sx={headCellSx} align="right">Gross</TableCell>
                  <TableCell sx={headCellSx} align="right">Additions</TableCell>
                  <TableCell sx={headCellSx} align="right">Deductions</TableCell>
                  <TableCell sx={headCellSx} align="right">LOP</TableCell>
                  <TableCell sx={headCellSx} align="right">Net Pay</TableCell>
                  <TableCell sx={{ ...headCellSx, width: "9rem" }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody><TableSkeleton columns={7} /></TableBody>
            </Table>
          </Box>
        </MainBox>
      </Box>
    );
  }
  if (!run) return <Box sx={{ width: "100%" }}><TopBar MainHeading={"Payroll"} /><EmptyState title="Payroll run not found" /></Box>;

  const pending = run.status === "pending_approval";
  const approved = run.status === "approved";
  const totals = runTotals(run);
  const warnings = runWarnings(run);
  const footer = columnTotals(run.items || []);
  /**
   * The register is paged, but the footer is not: it totals the whole run.
   * A totals row that only added up the visible page would be a different
   * number every time somebody clicked Next, on the one row of the screen that
   * exists to be reconciled.
   */
  const allItems = run.items || [];
  const currentPage = clampPage(page, allItems.length, rowsPerPage);
  const visibleItems = pageSlice(allItems, currentPage, rowsPerPage);

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Payroll"} breadcrumbs={[{ label: "Payroll", href: "/app/settings/payroll/runs" }, { label: periodLabel(run.month, run.year) }]} />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5} sx={{ mb: "1.25rem" }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ fontFamily: displayFont, fontSize: "1.25rem", fontWeight: 600, color: tokens.text }}>{periodLabel(run.month, run.year)}</Box>
            <StatusChip label={RUN_STATUS_LABEL[run.status] || run.status} tone={RUN_STATUS_TONE[run.status]} />
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <BtnSecondary to="/app/settings/payroll/runs" sx={{ borderRadius: "8px" }}>Back</BtnSecondary>
            <BtnSecondary onClick={exportCsv} sx={{ borderRadius: "8px" }}>Export CSV</BtnSecondary>
            {pending && run.runType !== "off_cycle" && (
              <BtnSecondary onClick={resync} disabled={submitting} sx={{ borderRadius: "8px" }}>Re-sync expenses</BtnSecondary>
            )}
            {pending && (
              <>
                <BtnSecondary onClick={() => review("rejected")} disabled={submitting} sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder }}>Reject</BtnSecondary>
                <BtnPrimary onClick={() => review("approved")} disabled={submitting} sx={{ borderRadius: "8px" }}>{submitting ? <CircularProgress size={"1.1rem"} /> : "Approve"}</BtnPrimary>
              </>
            )}
            {(approved || run.status === "paid") && <BtnSecondary onClick={exportBankFile} sx={{ borderRadius: "8px" }}>Bank file</BtnSecondary>}
            {approved && <BtnPrimary onClick={markPaid} disabled={submitting} sx={{ borderRadius: "8px" }}>{submitting ? <CircularProgress size={"1.1rem"} /> : "Mark as Paid"}</BtnPrimary>}
          </Stack>
        </Stack>

        {/* Five totals. Deductions reads danger and net reads success; the rest
            are ink. Employer cost is deliberately NOT marigold — marigold is
            reserved for the one action on this page that cannot be undone. */}
        <Box sx={{ ...counterRowSx(5), mb: "1.5rem" }}>
          <StatCard label="Employees" value={totals.people} />
          <StatCard label="Total gross" value={moneyExact(totals.gross)} />
          <StatCard label="Total deductions" value={moneyExact(totals.deductions)} accent={tokens.danger} />
          <StatCard label="Total net" value={moneyExact(totals.net)} accent={tokens.successText} />
          <StatCard label="Employer cost" value={moneyExact(totals.employer)} />
        </Box>
        {/*
          Gross minus deductions equalling net is the one piece of arithmetic
          anyone checks by hand, so it is stated rather than left to be inferred
          from five tiles. Employer cost sits after it, not inside it: it is a
          cost to the company on top of gross, not a deduction from anyone.
        */}
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.375rem", fontVariantNumeric: "tabular-nums" }}>
          {moneyExact(totals.gross)} gross − {moneyExact(totals.deductions)} deductions ={" "}
          <Box component="span" sx={{ color: tokens.successText, fontWeight: 700 }}>
            {moneyExact(totals.net)} net
          </Box>{" "}
          · employer cost {moneyExact(totals.employer)}, on top of gross
          {!totals.balances && (
            <Box component="span" sx={{ color: tokens.danger, fontWeight: 600 }}>
              {" "}· these do not reconcile
            </Box>
          )}
        </Box>

        {/* Ordered by how they block approval: review, then exclusions, then
            arithmetic that cannot stand. Counts come from the run object the
            register reads, so a warning and its rows cannot disagree. */}
        {warnings.map((w) => (
          <InlineMessage key={w.key} tone={w.tone} sx={{ mb: "0.625rem" }}>
            <b>{w.lead}</b> {w.rest}
          </InlineMessage>
        ))}
        {warnings.length > 0 && <Box sx={{ mb: "0.75rem" }} />}

        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headCellSx, width: "26%" }}>Employee</TableCell>
              <TableCell sx={headCellSx} align="right">Gross</TableCell>
              <TableCell sx={headCellSx} align="right">Additions</TableCell>
              <TableCell sx={headCellSx} align="right">Deductions</TableCell>
              <TableCell sx={headCellSx} align="right">LOP</TableCell>
              <TableCell sx={headCellSx} align="right">Net Pay</TableCell>
              <TableCell sx={{ ...headCellSx, width: "9rem" }}>Status</TableCell>
              {pending && <TableCell sx={{ ...headCellSx, width: "4rem" }} align="right">Edit</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleItems.map((it, pageIndex) => {
              // The expanded row is tracked by its index in the whole run, so
              // paging away and back does not open a different person's row.
              const i = (currentPage - 1) * rowsPerPage + pageIndex;
              const open = expanded === i;
              const ded = round((it.totalDeductions || 0) + (it.extraDeductions || 0));
              return (
                <React.Fragment key={i}>
                  <TableRow hover onClick={() => setExpanded(open ? null : i)}
                    sx={{ cursor: "pointer", "&:hover": { background: tokens.surfaceSubtle }, "& td": { borderBottom: open ? "none" : undefined } }}>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        {open ? <ExpandLessIcon sx={{ fontSize: "1.1rem", color: tokens.textMuted }} /> : <ExpandMoreIcon sx={{ fontSize: "1.1rem", color: tokens.textMuted }} />}
                        <span>{nameOf(it)}</span>
                        {it.edited && <Box component="span" title="Manually edited" sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.warningText, background: tokens.warningTint, borderRadius: "6px", px: 0.5, py: 0.5 }}>EDITED</Box>}
                        {it.variance && <VarianceBadge v={it.variance} />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={bodyCellSx} align="right">{moneyExact(it.grossPay)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: it.additions > 0 ? tokens.successText : tokens.textMuted }} align="right">{it.additions > 0 ? `+${moneyExact(it.additions)}` : "—"}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: tokens.danger }} align="right">{moneyExact(ded)}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, color: it.lopDays ? tokens.warningText : tokens.textMuted }} align="right">{it.lopDays ? `${it.lopDays}d · ${money(it.lopAmount)}` : "—"}</TableCell>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 600, color: it.negativeNet ? tokens.danger : tokens.text }} align="right">{money(it.netPay)}</TableCell>
                    <TableCell sx={bodyCellSx}>
                      {(() => {
                        const st = lineStatus(it, run.status);
                        return <StatusChip label={st.label} tone={st.tone} />;
                      })()}
                    </TableCell>
                    {pending && (
                      <TableCell sx={bodyCellSx} align="right" onClick={(e) => e.stopPropagation()}>
                        <IconButton aria-label="Edit" size="small" onClick={() => openEdit(it)} sx={{ color: tokens.textMuted }}><EditOutlinedIcon sx={{ fontSize: "1.05rem" }} /></IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                  {open && (
                    <TableRow>
                      <TableCell colSpan={pending ? 8 : 7} sx={{ p: 0, borderBottom: `1px solid ${tokens.border}` }}>
                        <BreakdownPanel it={it} />
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
          {/*
            "A register that does not add up is the thing being signed off."
            Every numeric column is summed, LOP days included — it is a count
            rather than money and still has to reconcile.
          */}
          <TableFooter>
            <TableRow sx={{ background: tokens.surface }}>
              <TableCell sx={FOOT_SX}>{footer.people} people</TableCell>
              <TableCell sx={FOOT_SX} align="right">{moneyExact(footer.gross)}</TableCell>
              <TableCell sx={FOOT_SX} align="right">
                {footer.additions > 0 ? moneyExact(footer.additions) : "—"}
              </TableCell>
              <TableCell sx={FOOT_SX} align="right">{moneyExact(footer.deductions)}</TableCell>
              <TableCell sx={FOOT_SX} align="right">{footer.lop ? `${footer.lop}d` : "—"}</TableCell>
              <TableCell sx={FOOT_SX} align="right">{moneyExact(footer.net)}</TableCell>
              <TableCell sx={FOOT_SX} />
              {pending && <TableCell sx={FOOT_SX} />}
            </TableRow>
          </TableFooter>
        </Table>

        <TablePager
          total={allItems.length}
          page={currentPage}
          rows={rowsPerPage}
          onPage={(p) => { setPage(p); setExpanded(null); }}
          onRows={(n) => { setRowsPerPage(n); setPage(1); setExpanded(null); }}
          noun="in this run"
          nounSingular="in this run"
        />

        <RunAuditPanel runId={id} refreshKey={run.status + (run.updatedAt || "")} />
      </MainBox>

      <Dialog open={Boolean(editItem)} onClose={() => setEditItem(null)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", width: "34rem", maxWidth: "94vw" } }}>
        {editItem && (
          <>
            <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "0.25rem" }}>{nameOf(editItem)}</Box>
            <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, mb: "1.25rem" }}>Gross {money(editItem.grossPay)} · base deductions {money(editItem.totalDeductions)}</Box>

            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Loss of Pay (days)</Box>
            <Box sx={{ maxWidth: "10rem", mb: "1.25rem" }}>
              <FormField type="number" value={lopDays} onChange={(e) => setLopDays(e.target.value)} fullwidth />
            </Box>

            <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Adjustments</Box>
            <Stack spacing={1}>
              {adjustments.map((a, idx) => (
                <Stack key={idx} direction="row" spacing={1} alignItems="center">
                  <Box sx={{ flex: 1.6 }}><FormField type="text" value={a.name} onChange={(e) => setAdjustments((p) => p.map((x, j) => j === idx ? { ...x, name: e.target.value } : x))} fullwidth /></Box>
                  <Box sx={{ flex: 1 }}><FormField type="number" value={a.amount} onChange={(e) => setAdjustments((p) => p.map((x, j) => j === idx ? { ...x, amount: e.target.value } : x))} fullwidth /></Box>
                  <Select value={a.type || "EARNING"} onChange={(e) => setAdjustments((p) => p.map((x, j) => j === idx ? { ...x, type: e.target.value } : x))} size="small" sx={{ ...selectSx, width: "8rem" }}>
                    <MenuItem value="EARNING">Earning</MenuItem>
                    <MenuItem value="DEDUCTION">Deduction</MenuItem>
                  </Select>
                  <IconButton size="small" onClick={() => setAdjustments((p) => p.filter((_, j) => j !== idx))} sx={{ color: tokens.danger }}><DeleteOutlineIcon sx={{ fontSize: "1.05rem" }} /></IconButton>
                </Stack>
              ))}
              <Box><TypeBtn type="add" onClick={() => setAdjustments([...adjustments, { name: "", amount: "", type: "EARNING" }])}>Add Adjustment</TypeBtn></Box>
            </Stack>

            <Box sx={{ mt: "1.25rem" }}>
              <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: "0.5rem" }}>Reason for change <Box component="span" sx={{ color: tokens.textMuted, fontWeight: 500 }}>(audited)</Box></Box>
              <FormField type="text" value={editReason} onChange={(e) => setEditReason(e.target.value)} fullwidth placeholder="e.g. correcting attendance LOP" />
            </Box>

            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: "2rem" }}>
              <BtnSecondary onClick={() => setEditItem(null)} disabled={submitting} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
              <BtnPrimary onClick={saveEdit} disabled={submitting} sx={{ borderRadius: "8px" }}>{submitting ? "Saving…" : "Save Line"}</BtnPrimary>
            </Stack>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PayrollRunDetail;
